/**
 * E2E renderer test probe — exposes deterministic discovery_mismatch (ENG-4322)
 * injection to Playwright via `globalThis.__irisDiscoveryMismatchProbe`.
 *
 * Per `think-in-systems` (ai.knowledge/global): CI cannot deterministically
 * drive REAL network discovery to produce a cross-protocol mislabel (that
 * requires two real discovery mechanisms disagreeing about one physical
 * device — the HIL lane's job, per the ENG-4322 acceptance contract). This
 * probe instead calls the EXACT same production code
 * (`detectDiscoveryMismatches` / `emitDiscoveryMismatches` from
 * discovery-mismatch.ts, and the real `trackDiscoveryMismatch` tracker bound
 * to the real `track()` pipeline) with synthetic Discovery objects supplied by
 * the test — so a regression in the classification logic, the tracker, or the
 * envelope/props reaches the exact same PostHog ingest boundary
 * `expectCanonicalEvent` observes, without touching broker/proxy internals.
 *
 * Mirrors the existing renderer-probe precedent at
 * `test-probes/device-probe.ts` — same IRIS_E2E_TEST=1 gate, same
 * single-statement assignment, same "test-only code path" intent.
 *
 * Per `stay-in-scope` (ai.knowledge/global): the surface is the minimum the
 * ENG-4322 CI e2e test needs — inject discoveries, read back what fired.
 * No additional methods.
 */
import type { AnalyticsTrackFn } from '#~/analytics/domains/types.js';
import { DISCOVERY_PROPERTIES } from '#~/analytics/constants/index.js';
import { createMediaControlsDiscoveryTracker } from '#~/analytics/domains/index.js';
import { track as trackEvent } from '#~/analytics/track.js';
import type { AnalyticsEvent } from '#~/analytics/types/events.js';
import {
  createDiscoveryMismatchDedupeState,
  emitDiscoveryMismatches
} from '#~/video/components/add/source/discovery-mismatch.js';
import type { Discovery } from '#~/video/components/add/source/types.js';

// Adapts the raw track(event) pipeline to the 3-arg AnalyticsTrackFn shape the
// domain trackers expect — the same adaptation useAnalytics() applies inside
// React (hooks/use-analytics.ts's trackEvent callback); this probe cannot use
// the hook because it runs outside a component.
const track: AnalyticsTrackFn = (name, category, properties) => {
  void trackEvent({ name, category, properties, timestamp: Date.now() } as AnalyticsEvent);
};

interface DiscoveryMismatchProbe {
  /**
   * Run the real classification + emission pipeline over synthetic
   * discoveries. Returns the number of NEW mismatches emitted (0 on a
   * repeated call with the same discoveries — the real single-fire dedupe).
   */
  inject(discoveries: Discovery[], surface?: string): number;
  /** Fresh dedupe state — call between test cases so mismatches re-fire. */
  reset(): void;
}

// Renderer-process gate. Bare `process.env.IRIS_E2E_TEST` does NOT work here:
// Vite's definePlugin unconditionally replaces `process.env` with `{}` for every
// non-lib client build (verified in this repo's own node_modules/vite
// dep-*.js: `"process.env": "{}"`), so that lookup is always undefined
// regardless of the real launch env. The real env reaches the renderer via
// @electron-toolkit/preload's `electronAPI.process.env`, exposed at
// `window.electron.process.env` (contextIsolation:false, see
// electron/types.d.ts) — the SAME bridge posthog/manager.ts's
// readE2EFeatureFlagOverrides() and electron/utils.ts's isElectronEnv already
// use for this exact IRIS_E2E_TEST check. Wrap in a try/catch so a missing
// `window`/`electron` bridge (e.g. workers) silently no-ops instead of
// crashing renderer init.
const isE2eEnabled = (): boolean => {
  try {
    return (
      (globalThis as { window?: { electron?: { process?: { env?: Record<string, string> } } } })
        .window?.electron?.process?.env?.IRIS_E2E_TEST === '1'
    );
  } catch {
    return false;
  }
};

if (isE2eEnabled()) {
  let dedupeState = createDiscoveryMismatchDedupeState();
  const tracker = createMediaControlsDiscoveryTracker(track);

  const probe: DiscoveryMismatchProbe = {
    inject(discoveries: Discovery[], surface = 'background_discovery'): number {
      return emitDiscoveryMismatches(discoveries, dedupeState, (mismatch) => {
        tracker.trackDiscoveryMismatch({
          device_id: mismatch.hostKey,
          surface,
          linking_surface: 'video_source_selector',
          // Same typed-const keys as the production emit site (selector.tsx), so
          // the probe cannot drift from the real property names it is meant to
          // stand in for.
          [DISCOVERY_PROPERTIES.PRIMARY_PROTOCOL]: mismatch.primaryProtocol,
          [DISCOVERY_PROPERTIES.SECONDARY_PROTOCOL]: mismatch.secondaryProtocol,
          [DISCOVERY_PROPERTIES.PRIMARY_NAME]: mismatch.primaryName,
          [DISCOVERY_PROPERTIES.SECONDARY_NAME]: mismatch.secondaryName
        });
      });
    },

    reset(): void {
      dedupeState = createDiscoveryMismatchDedupeState();
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (globalThis as any).__irisDiscoveryMismatchProbe = probe;
}
